import * as React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Box, Tabs } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { TimeSheetTabsLayoutDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'

export const TimesheetTabsLayout = ({ children }: React.PropsWithChildren) => {
  const { employee } = useCurrentSession()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { navigation } = useNavigation()
  const { employeeId, year, month, day } = useParams()
  const { pathParams: paramsFromDateNavigator } = useAttendanceDateNavigator()
  const isAdminView = pathname.includes('time-tracking')
  const employeeIdFromParams = Number(employeeId)
  const currentEmployeeId = Number(employee?.id)
  const { data, loading } = useQuery(TimeSheetTabsLayoutDocument, {
    variables: {
      employeeIds: [employeeIdFromParams ?? currentEmployeeId],
    },
  })

  const { featureAvailability: hasMyProjectsFeature } = useAttendanceFeatures(
    Features.projects_flexible_tracking
  )

  const projectsAssigned = !!data?.projectManagement.projectWorkers.edges.length

  const pathParams = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
  }

  const timesheetTabRef = isAdminView
    ? navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath({
        ...paramsFromDateNavigator,
        employeeId: employeeIdFromParams,
      })
    : navigation.clockIn.periodTypeYearMonthDay.fullPath(paramsFromDateNavigator)

  const projectsRef = navigation.timeTracking.employee.myProjects.yearMonthDayEmployeeId.fullPath({
    ...pathParams,
    employeeId: employeeIdFromParams,
  })

  const tabs = useMemo(() => {
    const tabs = [
      {
        label: i18n.t('time_tracking.timesheet.tabs.timesheet'),
        href: timesheetTabRef,
      },
    ]
    if (((hasMyProjectsFeature && projectsAssigned) || !hasMyProjectsFeature) && isAdminView) {
      tabs.push({
        label: i18n.t('time_tracking.timesheet.tabs.projects'),
        href: projectsRef,
      })
    }

    return tabs
  }, [year, month, day, employeeId, loading])

  return (
    <>
      <Box paddingBottom="s24">
        <Tabs
          key="time-tracking-timesheet-tabs"
          currentId={pathname}
          onTabClicked={(tabId) => navigate(tabId)}
        >
          {tabs.map((tab) => (
            <Tabs.Tab key={tab.href} id={tab.href} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      {children}
    </>
  )
}
